import React, { useEffect, useState, forwardRef, useCallback, useRef } from "react";
import styles from "./ChatArea.module.scss";
import { getIconComponent } from "../Prompts/PromptsCardList";
import { useModel } from "hooks/useModel";
import { useAppActions } from "components/ai-chat/store/AppStore";
import { useAxios } from "myAxios";

const QuickFavoritePrompts = forwardRef<
  HTMLDivElement,
  {
    open: boolean;
    onClose: () => void;
    setLocalInput: React.Dispatch<React.SetStateAction<string>>;
  }
>(({ open, onClose, setLocalInput }, ref) => {
  const { prompts, setPromptsOpen, currentAccountTags, setFullScreen } = useModel("aichat");
  const axios = useAxios();
  const { sendMessageFn } = useAppActions(axios);

  const [quickPrompts, setQuickPrompts] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const handleSend = useCallback(
    (item: any) => {
      onClose();
      setLocalInput("");
      sendMessageFn(item.promptContent, setLocalInput, currentAccountTags, {
        description: item.promptDescription,
        title: item.promptTitle,
        prompt: item.promptContent,
      });
    },
    [currentAccountTags, onClose, sendMessageFn, setLocalInput]
  );

  useEffect(() => {
    setQuickPrompts(prompts.find((item) => item.type === "favorite")?.list.slice(0, 10) || []);
  }, [prompts]);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
  }, [open, quickPrompts.length]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (quickPrompts.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % quickPrompts.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + quickPrompts.length) % quickPrompts.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }
        const item = quickPrompts[activeIndex];
        if (item) {
          handleSend(item);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open, quickPrompts, activeIndex, handleSend]);

  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector(
      `[data-quick-index="${activeIndex}"]`
    ) as HTMLElement | null;
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  return (
    <div
      className={`${styles.container} ${isVisible ? styles.visible : ""}`}
      ref={ref}
    >
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <span className={styles.title}>Favorite Prompt ({quickPrompts.length})</span>
          <span className={styles.shortcut}>Shortcut key '/'</span>
        </div>
        <a
          className={styles.moreLink}
          onClick={() => {
            setPromptsOpen(true);
            setFullScreen(true);
            onClose();
          }}
        >
          More Prompts &gt;
        </a>
      </div>

      {quickPrompts.length === 0 ? (
        <div className={styles.empty}>
          You have no favorite prompts yet. Go to "More Prompts" to add some!
        </div>
      ) : (
        <div
          className={styles.list}
          ref={listRef}
        >
          {quickPrompts.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              data-quick-index={index}
              className={`${styles.promptItem} ${
                activeIndex === index ? styles.promptItemActive : ""
              }`}
              onClick={() => {
                handleSend(item);
              }}
            >
              <span
                style={{
                  marginRight: 4,
                  fontSize: 14,
                  paddingTop: 2,
                }}
              >
                {getIconComponent(item?.promptIcon)}
              </span>
              <span className={styles.text}>{item.promptTitle}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

QuickFavoritePrompts.displayName = "QuickFavoritePrompts";

export default QuickFavoritePrompts;
